/**
 * 史実イベント — 第III章「天下布武」（1560-1581）
 *
 * docs/01-ゲームデザイン.md §7 の第三章の表に対応する。
 * 表にある5件（信長上洛・比叡山焼き討ち・室町幕府滅亡・長篠・安土城築城）に加え、
 * 合戦以外の出来事（楽市楽座・家督相続の内乱・長期戦の終結・儀礼としての馬揃え）を
 * 4件足して9件としてある。docs/05-学習設計.md §4-4 に従い、
 * 章の内容が合戦だけに偏らないようにするためである。
 *
 * 【この章の設計方針】
 * 第III章は「戦い方」ではなく「秩序の作り替え」の章である。
 * 室町幕府という枠組みが外れ（1573）、城が権威を可視化する装置になり（1576）、
 * 城下町の商いの規則が書き換えられる（1577）。
 * したがって各イベントの effects は、資源の増減で終わらせず、
 * できる限り「駒の位置・役の段階・手勢札の枚数・下剋上札・築城」を
 * 書き換える形にしている。
 *
 * 効果の多くを choice にしてあるのは、この章の出来事の大半が
 * 「どちらかが正しい」と決められない性質のものだからである。
 * 寺社領に手をつけるか、座の特権を残すか、包囲を続けるか — いずれも
 * ゲーム上の損得としては表せても、史実の当否としては評価しない。
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力（比叡山・一向一揆・キリスト教）は事実の記述に留める
 *   - 女性・民衆の存在を消さない。合戦以外の生活面の事実を必ず入れる
 *   - 通説と異説がある事項は caveat に必ず書く
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 *
 * 【本能寺の変について】
 * 本能寺の変(1582)は第IV章の担当であり、本ファイルには含まれない。
 * ただし本作がこれを「天下点1位が打撃を受ける」逆転装置にしている点は
 * 明智光秀の動機についての誤解を招きうるため、
 * 1581年の京都御馬揃え（光秀が奉行を務めた）の gameNote で断ってある。
 *
 * 【章の区切りについて】
 * 第III章は 1560-1581 だが、1560年の桶狭間は docs/01 §7 の第二章の表に
 * 置かれているため events/chapter2.ts に収めてある。本ファイルは 1568年から始まる。
 */

import type { HistoricalEvent } from '../types';

// ---------------------------------------------------------------------------
// 1568 — 信長上洛（時代法）
// ---------------------------------------------------------------------------

/**
 * 章の幕開け。京が全員の関心事になることを、駒の位置の書き換えで表す。
 * docs/01 §7 の「上洛の天下点が2倍」は倍率の Effect が無いため置き換えた。
 */
export const NOBUNAGA_JOURAKU: HistoricalEvent = {
  id: 'ev:nobunaga-jouraku',
  year: 1568,
  chapter: 3,
  kind: 'era-law',
  name: '信長上洛',
  reading: 'のぶながじょうらく',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '上洛の列に加わる（京へ駒を進め、天下点+2・兵-2）',
              then: [
                { t: 'moveTo', square: 0 },
                { t: 'gain', res: 'tenka', n: 2 },
                { t: 'lose', res: 'hei', n: 2 },
              ],
            },
            {
              label: '国もとの関所を撤廃し、流通を握る（銭+3・威信-1）',
              then: [
                { t: 'gain', res: 'zeni', n: 3 },
                { t: 'lose', res: 'ishin', n: 1 },
              ],
            },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1568年、織田信長は足利義昭を奉じて京へ入り、義昭は同年10月に第15代将軍となった。信長は前後して勢力圏の関所を撤廃し、人と物の往来にかかる負担を減らした。',
    detail: {
      1: '1568年、織田信長は足利義昭をつれて京都に入りました。義昭はこの年、室町幕府の15代目の将軍になりました。信長は、人やものの行き来のじゃまになっていた関所をなくし、商売をしやすくしました。',
      2: '永禄11年(1568)9月、織田信長は前将軍足利義輝の弟・足利義昭を奉じて上洛した。三好三人衆らが各地で抵抗したが退けられ、京は信長の影響下に入る。同年10月18日、義昭は第15代征夷大将軍に任じられ、室町幕府はいったん再建された形になった。信長はこの前後に尾張・美濃・南近江の関所を撤廃し、人と物の往来にかかる負担を減らしている。関所は寺社・公家・土豪にとって重要な収入源でもあったため、撤廃は流通政策であると同時に、既存の権益に手を入れる行為でもあった。',
      3: '永禄11年(1568)9月、織田信長は足利義昭を奉じて近江から入京した。義昭は永禄8年(1565)に兄・義輝が三好方に討たれたのち各地を頼り、越前朝倉氏のもとから信長を頼って上洛を果たす。三好三人衆らの抵抗は退けられ、10月18日に義昭は第15代征夷大将軍に任官した。『信長公記』には、信長が義昭から副将軍や管領代の地位を勧められたがこれを受けず、堺・大津・草津に代官を置くことを求めたとある。経済的な要地の掌握を優先したとみる理解が広く行われているが、この記述の解釈には幅がある。また信長は同年、尾張・美濃・南近江の関所を撤廃した。関所は寺社・公家・土豪の収入源であり、撤廃は流通の円滑化であると同時に、既存の権益への介入でもあった。撤廃の範囲は信長の勢力圏に限られ、全国の関がただちに消えたわけではない。翌永禄12年(1569)には義昭の居所として二条御所の造営が行われ、京の町衆や近郊の百姓が人夫として動員されている。義昭と信長の関係は当初は協調的で、決裂までには数年を要した。',
    },
    year: 1568,
    keywords: ['信長上洛', '足利義昭', '室町幕府', '関所撤廃', '永禄', '京'],
    caveat:
      '「信長が副将軍を断り、堺・大津・草津の代官職を求めた」という話は『信長公記』に見えるものですが、その意味づけには解釈の幅があります。また上洛を「天下統一への第一歩」と説明することが多い一方、当時の「天下」は畿内を指す語だったとする研究があり、この時点で全国統一を構想していたかどうかは確かめられていません。関所の撤廃も信長の勢力圏に限られた措置でした。',
    gameNote:
      'docs/01 §7 では「上洛の天下点が2倍」となっていますが、Effect に倍率の仕組みがないため、全員が「京へ上る／国もとを固める」を選ぶ形に置き換えました。駒を京(0)へ移すのは、上洛が諸大名の関心事になったことを盤上で表したものです。実際に上洛したのは信長方であり、全大名が同時に京を目指したわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1571 — 比叡山焼き討ち（事件札）
// ---------------------------------------------------------------------------

/**
 * 宗教勢力を「敵」として描かないため、効果は「手をつける／つけない」の選択にした。
 * どちらにも損があり、どちらが正しいとも書かない。
 */
export const HIEIZAN_YAKIUCHI: HistoricalEvent = {
  id: 'ev:hieizan-yakiuchi',
  year: 1571,
  chapter: 3,
  kind: 'incident',
  name: '比叡山焼き討ち',
  reading: 'ひえいざんやきうち',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '寺社領に兵を入れて糧を得る（銭+3・威信-2、守りの役が1段階下がる）',
          then: [
            { t: 'gain', res: 'zeni', n: 3 },
            { t: 'lose', res: 'ishin', n: 2 },
            { t: 'holeCardMod', n: -1, scope: 'defend' },
          ],
        },
        {
          label: '寺社領には手をつけず、山麓の町を保つ（威信+2・銭-1）',
          then: [
            { t: 'gain', res: 'ishin', n: 2 },
            { t: 'lose', res: 'zeni', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1571年、織田信長の軍が近江の比叡山延暦寺を攻め、根本中堂などの堂舎と山麓の坂本の町を焼いた。延暦寺が浅井・朝倉方に与し、信長の中立要求に応じなかったことが直接の理由とされる。',
    detail: {
      1: '1571年、織田信長の軍が、近江（いまの滋賀県）にある比叡山延暦寺をせめて、たてものを焼きました。延暦寺が、信長と対立する大名の味方をしたからだといわれます。ふもとの坂本の町も焼け、多くの人がなくなりました。',
      2: '元亀2年9月12日（西暦1571年9月30日）、織田信長の軍が近江国の比叡山延暦寺を攻め、根本中堂をはじめとする堂舎と、山麓の坂本の町を焼いた。前年の志賀の陣で延暦寺が浅井長政・朝倉義景の軍を山内に受け入れ、信長の中立要求に応じなかったことが直接の理由とされる。当時の延暦寺は広大な荘園を持ち、日吉社の神人を通じて金融や流通にも関わり、僧兵を擁する政治勢力でもあった。『言継卿記』などは数千人が殺されたと伝えるが、近年の発掘調査からは、焼失の範囲をもっと限定的にみる指摘が出ている。',
      3: '元亀2年9月12日（西暦1571年9月30日）、織田信長は比叡山延暦寺を攻撃し、根本中堂・大講堂などの堂舎および山麓の坂本の町を焼いた。背景には、元亀元年(1570)の志賀の陣で延暦寺が浅井長政・朝倉義景の軍を山内に受け入れ、信長の中立要求にも応じなかったことがある。延暦寺は近江・山城を中心に広大な荘園を持ち、日吉社の神人を通じて金融・流通に関与し、僧兵を擁する領主的な存在でもあった。被害の規模については、『言継卿記』『御湯殿上日記』などが堂塔500余棟の焼失と僧俗男女3000人余の殺害を伝える。しかし昭和後期以降の発掘調査では、この焼き討ちによる焼失が明確に指摘できる建物は根本中堂・大講堂などに限られ、他の地点で確認される焼土層の多くは焼き討ち以前に廃絶した建物のものであるとする指摘がある（兼康保明ら）。出土遺物も平安期のものが目立ち、16世紀の遺物は坂本城跡に比べて少ない。したがって伝えられる数字には誇張が含まれる可能性が高いが、調査範囲は東塔・西塔・横川の一部にとどまり、全容が判明したわけではない。焼かれた坂本は琵琶湖の湖上交通と延暦寺の門前として栄えた町で、商人・職人・馬借のほか、市に立つ女性を含む住民が暮らしていた。この出来事は「信長による宗教弾圧」として語られることが多いが、当時の大寺院は武装した領主でもあり、軍事・政治上の対立として捉える見方が現在は有力である。',
    },
    year: 1571,
    keywords: ['比叡山', '延暦寺', '坂本', '志賀の陣', '寺社勢力', '発掘調査'],
    caveat:
      '『言継卿記』などが伝える「堂塔500余棟が焼け、僧俗男女3000人余が殺された」という数字は、近年の発掘調査の結果と整合せず、誇張を含む可能性が指摘されています。焼失が確認できる建物は根本中堂・大講堂などに限られ、他の多くは焼き討ち以前に廃絶していたとする研究があります。ただし調査は山内の一部にとどまり、被害が小さかったと確定したわけでもありません。また、この出来事を「無神論者の信長による宗教弾圧」と説明する例がありますが、当時の延暦寺は荘園と武力を持つ領主でもあり、軍事・政治上の対立として理解する見方が現在は有力です。',
    gameNote:
      'docs/01 §7 では「寺社マスの威信が半減、代わりに略奪可」としていますが、Effect にマスの効果そのものを書き換える仕組みがないため、引いた者が「手をつける／つけない」を選ぶ形にしました。手をつけると守りの役が1段階下がるのは、ゲームの均衡を取るための設定であって、信仰の当否や行為の善悪を評価するものではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1573 — 室町幕府の滅亡（時代法）
// ---------------------------------------------------------------------------

/**
 * 「将軍」カード全廃は Effect で表せないため、
 * 「官位由来の威信が失効し、代わりに下剋上札が配られる」形に置き換えた。
 * 枠組みが外れて実力の争いになる、という章の主題そのものを担う時代法。
 */
export const MUROMACHI_BAKUFU_METSUBOU: HistoricalEvent = {
  id: 'ev:muromachi-bakufu-metsubou',
  year: 1573,
  chapter: 3,
  kind: 'era-law',
  name: '室町幕府の滅亡',
  reading: 'むろまちばくふのめつぼう',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        { t: 'lose', res: 'ishin', n: 2 },
        { t: 'grantGekokujo', n: 1 },
      ],
    },
  ],
  note: {
    summary:
      '1573年、足利義昭は織田信長と対立して槇島城で挙兵したが敗れ、京を追われた。これをもって室町幕府の滅亡とするのが通説だが、義昭はその後も征夷大将軍の位にあった。',
    detail: {
      1: '1573年、15代将軍の足利義昭は、織田信長と対立して兵をあげましたが、まけて京都から追い出されました。ふつうは、この年に室町幕府がほろびたとされます。ただし義昭はそのあとも将軍のままで、西国から各地の大名に手紙を出しつづけました。',
      2: '元亀4年（天正元年・1573）、足利義昭は信長との対立を深め、2月に二条御所で、7月に山城の槇島城で挙兵したが、いずれも敗れて京都を追われた。一般にはこれをもって室町幕府の滅亡とする。ただし義昭は将軍職を辞したわけではなく、のちに毛利氏を頼って備後の鞆に移り、諸大名へ御内書を発して信長に対抗する勢力を結びつけようとした。義昭が正式に征夷大将軍を辞したのは天正16年(1588)である。同じ1573年には浅井氏・朝倉氏も滅び、7月には元亀から天正へ改元された。信長は以後、将軍を戴かずに畿内の秩序を組み立てていくことになる。',
      3: '元亀4年(1573)、足利義昭と織田信長の関係は破綻し、義昭は2月に二条御所で、7月に山城国槇島城で挙兵したが、いずれも信長の軍に屈して7月に京都を追われた。通説はこの追放をもって室町幕府の滅亡とする。しかし義昭は征夷大将軍の職を失っておらず、河内・紀伊を経て天正4年(1576)に毛利氏の勢力下にある備後鞆へ移り、そこから諸大名へ御内書を発して反信長の連携を働きかけた。藤田達生は『公卿補任』などを根拠に、義昭が天正16年(1588)に正式に将軍を辞するまで幕府の機構が鞆で機能していたとし、これを「鞆幕府」と呼ぶ。この見方に対しては、亡命政権を幕府と呼びうるかをめぐって異論もあり、滅亡の年は一つに定まっていない。同年には信長包囲網の中核であった浅井長政と朝倉義景が相次いで滅び、7月28日に元亀から天正へ改元された。義昭追放後の信長は将軍の権威に依らずに畿内を編成することになるが、朝廷から官位を受けつつ天正6年(1578)には右大臣・右近衛大将を辞するなど、既存の権威との距離の取り方は一様ではない。なお義昭は追放後に出家して昌山と号し、豊臣政権下では大名並みの待遇を受けて慶長2年(1597)に没している。',
    },
    year: 1573,
    keywords: ['室町幕府', '足利義昭', '槇島城', '鞆幕府', '天正改元', '将軍'],
    caveat:
      '「1573年に室町幕府が滅びた」は広く用いられる通説ですが、足利義昭は追放後も1588年まで征夷大将軍の位にあり、備後鞆で御内書を発して幕府の機能を維持していたとする研究（藤田達生の「鞆幕府」論）があります。これを幕府の存続とみるかについては議論が分かれており、滅亡の年は一つに定まっていません。',
    gameNote:
      'docs/01 §7 では「『将軍』カード全廃」となっていますが、Effect にカードを廃止する仕組みがないため、全員が官位に由来する威信を失い、代わりに下剋上札を1枚得る形にしました。権威の枠組みが外れて実力の争いになることを盤上に置き換えたものです。docs/06 §6 では下剋上札の配布は順位に応じて行いますが、この時代法だけは全員に1枚配ります。',
  },
};

// ---------------------------------------------------------------------------
// 1575 — 長篠の戦い（時代法）
// ---------------------------------------------------------------------------

/**
 * docs/06 §4 の補正表「長篠以降の鉄砲 +1」（手勢札）を解禁する時代法。
 * 無条件にせず銭を払う選択にしたのは、鉄砲と硝石が高価だったため。
 */
export const NAGASHINO: HistoricalEvent = {
  id: 'ev:nagashino',
  year: 1575,
  chapter: 3,
  kind: 'era-law',
  name: '長篠の戦い',
  reading: 'ながしののたたかい',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '鉄砲と硝石を買い揃える（銭-3。以後、軍配勝負の手勢札 +1）',
              then: [
                { t: 'lose', res: 'zeni', n: 3 },
                { t: 'holeCardMod', n: 1 },
              ],
            },
            {
              label: '馬と槍で戦う支度をする（兵+2）',
              then: [{ t: 'gain', res: 'hei', n: 2 }],
            },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1575年、三河の設楽原で織田信長・徳川家康の連合軍が武田勝頼の軍と戦い、武田方は多くの将を失った。多数の鉄砲と馬防柵が用いられた戦いとして知られる。',
    detail: {
      1: '1575年、いまの愛知県の設楽原（したらがはら）で、織田信長と徳川家康の軍が、武田勝頼の軍と戦いました。織田・徳川がわは、たくさんの鉄砲と、馬をふせぐ柵をつかいました。武田がわは多くの武将をうしないました。',
      2: '天正3年5月21日（西暦1575年6月29日）、三河国の長篠城をめぐって、織田信長・徳川家康の連合軍と武田勝頼の軍が設楽原で戦った。連合軍は連吾川沿いに馬防柵と土塁を築き、多数の鉄砲を配して武田方の攻撃を受け止め、武田方は山県昌景・馬場信春・内藤昌豊ら多くの将を失った。以後、武田氏は勢いを取り戻せないまま1582年に滅びる。「三千挺の鉄砲を三段に分けて交互に撃った」という説明が広く知られているが、これは後世の軍記に由来するもので、一次史料に近い『信長公記』には三段撃ちの記述がない。',
      3: '天正3年5月21日（西暦1575年6月29日）、三河国長篠城の救援に向かった織田信長・徳川家康の連合軍と、城を囲んでいた武田勝頼の軍が設楽原で交戦した。連合軍は連吾川沿いに馬防柵と土塁を築いて陣を敷き、多数の鉄砲を配置した。武田方は山県昌景・馬場信春・内藤昌豊ら重臣を多く失い、以後、勢力を回復できないまま天正10年(1582)に滅亡する。鉄砲の数については『信長公記』の写本間に異同があり、「鉄炮千挺」とする本と「鉄炮三千余挺」とする本がある。平山優は尊経閣文庫本などが「三千」と明記することを指摘するが、一方で「三」が後から書き加えられた形跡のある写本も知られており、確定していない。広く知られる「三段撃ち」は小瀬甫庵の『甫庵信長記』に見えるもので、『信長公記』には記述がなく、他の同時代史料にも裏付けがない。近年は、勝敗の要因を射撃技術ではなく、陣地の構築、長篠城の確保、兵站、そして武田方が攻勢に出ざるを得なかった政治的事情に求める見方が主流である。「鉄砲が騎馬隊を破って戦術が革命的に変わった」という図式も、鉄砲がすでに各地で用いられていたこと、武田軍が騎馬中心の軍だったとする理解自体が見直されていることから、慎重に扱われる。合戦の場となった設楽原一帯は田植えの時期にあたり、周辺の村は避難と徴発を強いられている。',
    },
    year: 1575,
    keywords: ['長篠', '設楽原', '鉄砲', '馬防柵', '武田勝頼', '三段撃ち'],
    caveat:
      '「三千挺の鉄砲を三段に分けて交互に撃った」という説明は江戸初期の『甫庵信長記』に由来し、一次史料に近い『信長公記』には見えません。鉄砲の数も写本によって「千挺」「三千余挺」と異なり、確定していません。また「鉄砲隊が武田の騎馬隊を破って戦術が一変した」という図式も、鉄砲がすでに各地で使われていたこと、武田軍が騎馬中心だったとする理解自体が見直されていることから、現在は慎重に扱われています。',
    gameNote:
      'docs/01 §7 と docs/06 §4 では、長篠以降「鉄砲札を持つ側の手勢札が1枚増える」ことになっています。本イベントの holeCardMod はその解禁を表します。docs/06 §4 は「役を1段階上げる」形の補正を実測の結果として捨てているため、常設の補正である本イベントも手勢札の枚数で効かせます。無条件にせず「銭を払って鉄砲と硝石を揃える」選択にしたのは、鉄砲が高価で、火薬の原料である硝石を輸入に頼っていたためです。史実の長篠を境に全大名が鉄砲を揃えたわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1576 — 安土城築城（時代法）
// ---------------------------------------------------------------------------

/**
 * docs/01 §7 の「『天守』の建設が解禁」に対応する。
 * 城が防御施設であると同時に権威を見せる装置になった、という転換を扱う。
 */
export const AZUCHIJOU: HistoricalEvent = {
  id: 'ev:azuchijou',
  year: 1576,
  chapter: 3,
  kind: 'era-law',
  name: '安土城築城',
  reading: 'あづちじょうちくじょう',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '天主を上げる（銭-5。守りの役が2段階上がる天守を築く）',
              then: [
                { t: 'lose', res: 'zeni', n: 5 },
                { t: 'build', level: 'tenshu' },
              ],
            },
            {
              label: '費えを惜しみ、兵を養う（兵+3）',
              then: [{ t: 'gain', res: 'hei', n: 3 }],
            },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1576年、織田信長は近江の安土山に築城を始め、1579年に完成した天主へ移った。総石垣の上に高い天主を上げた城で、以後の城のあり方に影響を与えたとされる。',
    detail: {
      1: '1576年、織田信長は琵琶湖のそばの安土山に、大きな城をつくりはじめました。1579年には高い天主（天守）ができて、信長はそこにうつりすみました。石がきを高くつみ、かべや屋根をかざった、それまでにない城でした。',
      2: '天正4年(1576)正月、織田信長は丹羽長秀を総普請奉行として、近江国の安土山に築城を始めた。天正7年(1579)5月、完成した天主に信長が移り住んだ。安土城は琵琶湖の水運と街道をあわせて押さえる位置にあり、総石垣の上に五重の天主を上げ、内部は狩野永徳一門の障壁画で飾られたと伝えられる。城下には家臣の屋敷と町が置かれ、翌1577年には楽市の掟書が出された。普請には近隣の村から百姓が人夫として動員され、石材は湖上を舟で運ばれている。安土城は1582年、本能寺の変の直後に焼失した。',
      3: '天正4年(1576)正月中旬、織田信長は丹羽長秀を総普請奉行に任じ、近江国蒲生郡の安土山で築城を開始した。安土山はかつて六角氏の観音寺城の支城が置かれた地で、琵琶湖の水運と東山道・北国街道をあわせて押さえる位置にある。天正7年(1579)5月、完成した天主に信長が移った。安土城は総石垣で築かれ、大手道が直線的な石段として山上へ伸び、山上には五重（内部は地上六階地下一階と伝わる）の天主が上がった。天主の意匠については『信長公記』や宣教師の記録に断片的な記述があり、狩野永徳一門の障壁画や金箔瓦の使用が知られるが、外観の復元案は研究者によって大きく異なり、確定していない。安土城は、山城の防御性と、権威を可視化する高層建築とを併せ持つ点で画期とされ、以後の近世城郭の天守に影響したとみられている。ただし高石垣や高層の櫓建築には先行例があり、安土城のみを起点とする説明は単純化を含む。築城には近隣の村々から人夫が徴発され、石材は湖上を舟で運ばれた。城下には家臣団の屋敷と町が置かれ、天正5年(1577)には安土山下町中掟書が出されている。安土城は天正10年(1582)、本能寺の変の直後に焼失した。焼失の原因については、明智方の兵によるとする説、織田信雄によるとする説、混乱のなかの失火とする説などがあり、定まっていない。',
    },
    year: [1576, 1579],
    keywords: ['安土城', '天主', '総石垣', '丹羽長秀', '近世城郭', '琵琶湖'],
    caveat:
      '安土城の天主の外観は、絵図や記録が断片的にしか残らないため、復元案が研究者ごとに大きく異なります。「日本で最初の天守」と説明されることがありますが、高石垣や高層の櫓建築には先行例があり、安土城だけを起点とするのは単純化です。1582年の焼失の原因も、明智方の兵によるとする説、織田信雄によるとする説などがあって確定していません。',
    gameNote:
      'docs/01 §7 の「『天守』の建設が解禁」を、全員が「天主を上げるか、費えを惜しむか」を選ぶ形にしました。実際に安土城を築いたのは信長で、他の大名が同時に天主を上げたわけではありません。天守が各地に広まるのは豊臣政権期以降です。銭5という費用は遊びの均衡のための値で、実際の普請費用とは関係ありません。',
  },
};

// ---------------------------------------------------------------------------
// 1577 — 安土山下町中掟書（事件札）
// ---------------------------------------------------------------------------

/**
 * 合戦以外の側面を担う一枚。docs/05 §4-4 に従い、
 * 城下で生業を営んだ商人・職人・女性の存在を detail に必ず入れている。
 */
export const EV_RAKUICHI_RAKUZA: HistoricalEvent = {
  id: 'ev:rakuichi-rakuza',
  year: 1577,
  chapter: 3,
  kind: 'incident',
  name: '安土山下町中掟書',
  reading: 'あづちさんかちょうちゅうおきてがき',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '城下を楽市とし、座と関の権益を手放す（銭+3・札を1枚引く・威信-1）',
          then: [
            { t: 'gain', res: 'zeni', n: 3 },
            { t: 'draw', n: 1 },
            { t: 'lose', res: 'ishin', n: 1 },
          ],
        },
        {
          label: '座と関を残し、従来の収入を守る（銭+1・軍略+1）',
          then: [
            { t: 'gain', res: 'zeni', n: 1 },
            { t: 'gain', res: 'gunryaku', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1577年、織田信長は安土の城下に十三か条の掟書を出し、座の特権や諸役を免除して誰でも商いができるようにした。楽市楽座と呼ばれる政策の代表例。',
    detail: {
      1: '1577年、織田信長は安土の城下町に「だれでも自由に商売してよい」という決まりを出しました。それまでは座という組合に入らないと商売がしにくかったのですが、その特権をなくしました。人やものが集まり、町はにぎわいました。',
      2: '天正5年(1577)6月、織田信長は安土の城下に十三か条の「安土山下町中掟書」を出した。当町を楽市として座の特権や諸役・諸公事を免除し、街道を往来する商人が城下に必ず宿を取るよう定め、火災や罪科の連座を限り、徳政の適用を除くなどの内容を含む。こうした政策は楽市楽座と呼ばれる。ただし楽市令は信長の発明ではなく、六角氏が近江石寺で、今川氏が駿河で、斎藤氏が美濃加納で先に出しており、信長自身も1567年に加納で出している。城下では、米や塩を扱う商人や鍛冶・大工などの職人のほか、機を織り、市に立ち、宿や酒屋を営む女性たちも生業を営んだ。',
      3: '天正5年(1577)6月、信長は安土城下に十三か条からなる「安土山下町中掟書」を発した。内容は、当町を楽市として諸座・諸役・諸公事を免除すること、上街道を往来する商人に城下での宿泊を義務づけること、火災や罪科の連座を限定すること、徳政の適用を除外すること、普請役を軽減することなどにわたる。座の特権を否定して新規参入を認める一方、街道の通行を城下へ引き込み、徳政を及ぼさないことで金融と取引の安定を図る、都市育成の政策として理解されている。楽市令は信長の創始ではなく、天文18年(1549)の六角定頼による近江石寺の例をはじめ、今川氏・斎藤氏の例が先行し、信長自身も永禄10年(1567)に美濃加納で出している。またこれらの令は特定の新興城下町に適用されたもので、既存の座や関がただちに全国で消滅したわけではない。近年の研究は、信長の経済政策を「中世的特権の一掃」と一括りにすることに慎重で、必要に応じて座を保護した事例も指摘されている。城下では、商人や職人だけでなく、宿・酒屋を営む者、機を織り市で商う女性たちが日々の生業を担っており、都市の実態はそうした人びとによって支えられていた。掟書が定める連座の限定や徳政除外は、こうした住民の生活と取引の安定に直接関わる条項である。',
    },
    year: 1577,
    keywords: ['楽市楽座', '安土', '城下町', '座', '掟書', '町人'],
    caveat:
      '楽市楽座を「信長が始めた画期的な政策」と説明することがありますが、六角氏・今川氏・斎藤氏に先行例があり、信長も1567年の美濃加納で先に出しています。また掟書が適用されたのは特定の城下町で、全国の座や関がただちに消えたわけではありません。近年は信長が座を保護した事例も知られており、政策を一律に「中世的特権の否定」とまとめることには慎重な見方があります。',
    gameNote:
      '引いた者だけが選ぶ事件札にしていますが、実際の掟書は安土の城下という限られた範囲に出されたものです。「札を1枚引く」という効果は、人と物が集まって選べる手が増えることを表しています。座を残す側にも利があるようにしてあるのは、どちらか一方を正しい政策として示さないためです。',
  },
};

// ---------------------------------------------------------------------------
// 1578 — 御館の乱（事件札）
// ---------------------------------------------------------------------------

/**
 * 合戦ではなく「家督相続」で国が割れる例。
 * 婚姻で結ばれた女性たちが両陣営にまたがっていた点を detail に入れている。
 */
export const OTATE_NO_RAN: HistoricalEvent = {
  id: 'ev:otate-no-ran',
  year: 1578,
  chapter: 3,
  kind: 'incident',
  name: '御館の乱',
  reading: 'おたてのらん',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '家督争いの一方に兵を送る（兵-3。空白の国を1つ無償で切り取る）',
          then: [
            { t: 'lose', res: 'hei', n: 3 },
            { t: 'seize', free: true },
          ],
        },
        {
          label: '静観して備えを固める（軍略+2・威信+1）',
          then: [
            { t: 'gain', res: 'gunryaku', n: 2 },
            { t: 'gain', res: 'ishin', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1578年、越後の上杉謙信が急死し、二人の養子・上杉景勝と上杉景虎が家督を争って越後は内乱となった。上杉氏は多くの家臣と所領を失った。',
    detail: {
      1: '1578年、越後（いまの新潟県）の上杉謙信が、とつぜん亡くなりました。あとつぎを決めていなかったため、二人の養子が家をつぐことを争い、越後は二つにわれて戦いになりました。これを御館の乱といいます。',
      2: '天正6年3月13日（西暦1578年4月19日）、越後の上杉謙信が春日山城で急死した。後継を明示しないままだったため、養子の上杉景勝（謙信の姉の子）と上杉景虎（北条氏康の子で、同盟にともなって越後へ入っていた）が家督を争い、翌年にかけて越後を二分する内乱となった。景虎が拠った御館（前関東管領上杉憲政の居館）の名から御館の乱と呼ばれる。天正7年(1579)に景勝が勝ったが、上杉氏は多くの家臣と所領を失い、北条氏との同盟も断たれた。この混乱の間に、織田方は北陸で勢力を伸ばしている。',
      3: '天正6年3月13日（西暦1578年4月19日）、上杉謙信は越後春日山城で急死した。数日前に倒れて意識が戻らなかったと伝えられ、脳血管障害を疑う見方があるが、死因は史料からは確定できない。謙信は実子を持たず、家督を明示しないまま没したため、養子の上杉景勝と上杉景虎が争った。景勝は謙信の姉・仙桃院と長尾政景の子、景虎は北条氏康の子で、永禄12年(1569)の越相同盟にともなって越後へ入っていた。景勝は春日山城の本丸と金蔵を早期に押さえ、景虎は前関東管領上杉憲政の居館である御館に拠った。景虎方には北条氏政が加担し、当初は武田勝頼も動いたが、勝頼は景勝と甲越同盟を結んで撤退し、これが武田・北条の関係を悪化させる。天正7年(1579)3月に御館は落ち、景虎は自害した。乱は上杉氏の家臣団と所領を大きく損ない、北陸方面での織田勢の進出を許す一因となった。仙桃院や、景勝・景虎それぞれと婚姻で結ばれた女性たちが両陣営にまたがっており、家督争いが同時に姻戚関係の分裂でもあったことは、この内乱の性格をよく示している。なお謙信については「毘沙門天の生まれ変わりを自任した」「私利のために戦わなかった」といった人物像が知られるが、その多くは江戸期以降に整えられた語りであり、関東・北陸への出兵は領国の利害と密接に結びついていた。',
    },
    year: [1578, 1579],
    keywords: ['御館の乱', '上杉謙信', '上杉景勝', '上杉景虎', '越後', '家督相続'],
    caveat:
      '上杉謙信の死因は史料から確定できません。厠で倒れたという記述から脳血管障害を推測する説が広く紹介されていますが、確かめる手立てはなく、毒殺説なども根拠を欠きます。また「義のために戦った謙信」という人物像の多くは江戸時代以降に整えられたもので、実際の出兵は領国の利害と結びついていました。',
    gameNote:
      '家督争いを「兵を送って介入する／静観する」の二択にしました。介入して空白の国を無償で切り取れるのは、内乱に乗じて近隣勢力が動いた事実を単純化したものです。実際に御館の乱へ介入したのは北条氏と武田氏であり、盤上のように誰でも介入できたわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1580 — 石山合戦の終結（事件札）
// ---------------------------------------------------------------------------

/**
 * 10年に及んだ対陣の終わり。
 * 一向一揆を「宗教勢力の反乱」とだけ描かないよう、在地の利害に触れている。
 * 包囲を選ぶと手勢札が減るのは、長期戦の消耗をルールに落としたもの。
 */
export const ISHIYAMA_KASSEN_SHUUKETSU: HistoricalEvent = {
  id: 'ev:ishiyama-kassen-shuuketsu',
  year: 1580,
  chapter: 3,
  kind: 'incident',
  name: '石山合戦の終結',
  reading: 'いしやまかっせんのしゅうけつ',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '勅命の講和を仲立ちする（威信+3・銭-2）',
          then: [
            { t: 'gain', res: 'ishin', n: 3 },
            { t: 'lose', res: 'zeni', n: 2 },
          ],
        },
        {
          label: '包囲を続けて明け渡させる（銭+2・兵-2。次の合戦で手勢札が1枚減る）',
          then: [
            { t: 'gain', res: 'zeni', n: 2 },
            { t: 'lose', res: 'hei', n: 2 },
            { t: 'holeCardMod', n: -1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1570年から10年続いた織田信長と本願寺の戦いが、1580年に朝廷の仲介による講和で終わった。本願寺の顕如は石山を退去し、大坂の地は信長方に渡った。',
    detail: {
      1: '1570年から10年ものあいだ、織田信長と、大坂の石山本願寺にこもる浄土真宗の人たちが戦っていました。1580年、天皇の仲だちで話し合いがまとまり、本願寺の顕如は石山を出ました。ながい戦いがおわりました。',
      2: '元亀元年(1570)から天正8年(1580)まで、織田信長と石山本願寺（大坂）の浄土真宗本願寺派が争った。これを石山合戦という。法主の顕如は各地の門徒に決起を促し、浅井・朝倉・武田・毛利ら反信長勢力と結んだため、戦いは畿内・東海・北陸・西国に及び、和睦と再戦を繰り返した。天正8年閏3月、正親町天皇の勅命による講和が成立し、顕如は石山を退去した。長男の教如は籠城を続けたが、8月に退去し、直後に石山は焼けている。この10年の間に、伊勢長島や越前など各地の一向一揆でも多数の死者が出た。',
      3: '元亀元年9月(1570)から天正8年8月(1580)まで、織田信長と石山本願寺を拠点とする浄土真宗本願寺派の間で断続的に続いた戦いを石山合戦と呼ぶ。法主の顕如は諸国の門徒に檄を発し、浅井長政・朝倉義景・武田信玄・毛利輝元ら反信長勢力と連携したため、戦線は摂津・河内から伊勢長島、越前、加賀、瀬戸内へと広がった。石山本願寺は淀川の河口に近い上町台地にあり、堀と土塁に囲まれた寺内町を抱える要害で、毛利水軍による海上補給が籠城を支えた。天正4年(1576)の第一次木津川口の海戦では毛利水軍が織田水軍を破ったが、天正6年(1578)の第二次では九鬼嘉隆の大型船が毛利水軍を退け、補給路が細る。天正8年閏3月5日（西暦1580年4月18日）、正親町天皇の勅命による講和が成立し、顕如は紀伊鷺森へ退去した。これに反対した長男の教如は籠城を続けたが、8月2日に石山を明け渡し、同月下旬に退去した。直後に石山は焼失している。顕如と教如の対立をのちの本願寺の東西分立に直結させる説明があるが、分立は慶長7年(1602)に至る複数の経緯を経たものであり、単純化にあたる。合戦の期間、伊勢長島(1574)や越前(1575)では大規模な殺戮が記録されており、門徒には農民・商人・職人・女性を含む多くの人びとが加わっていた。一向一揆を「宗教勢力の反乱」とのみ捉えると、年貢の負担や在地の自治をめぐる利害という、もう一つの側面が見えなくなる。',
    },
    year: [1570, 1580],
    keywords: ['石山合戦', '本願寺', '顕如', '教如', '一向一揆', '勅命講和'],
    caveat:
      '「石山本願寺」「石山合戦」という呼び名は後世に定着したもので、同時代の史料では「大坂本願寺」などと記されます。「石山」という語の由来にも議論があります。また顕如と教如の対立をそのまま後の東西本願寺分立の原因と説明することがありますが、分立は1602年に至る経緯を経たもので、直結させるのは単純化です。一向一揆の犠牲者数も、軍記や後世の記録に依る数字が多く、実数は分かっていません。',
    gameNote:
      '10年に及ぶ対陣を「講和を仲立ちする／包囲を続ける」の二択にしました。包囲を選ぶと次の合戦で手勢札が1枚減るのは、長期の対陣が兵と兵糧を消耗させたことを盤上に置いたものであり、どちらの側の正しさを示すものでもありません。実際の講和は朝廷の勅命によるもので、他の大名が仲立ちしたわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1581 — 京都御馬揃え（事件札・章の締めくくり）
// ---------------------------------------------------------------------------

/**
 * 戦わずに力を示す催し。合戦以外の政治手段を一枚入れておく。
 * 第IV章の本能寺の変（首位が打撃を受ける機構）についての断りを
 * gameNote に置いてある。奉行が明智光秀だったための配置。
 */
export const KYOTO_ONMAZOROE: HistoricalEvent = {
  id: 'ev:kyoto-onmazoroe',
  year: 1581,
  chapter: 3,
  kind: 'incident',
  name: '京都御馬揃え',
  reading: 'きょうとおんうまぞろえ',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '装いを整えて列に加わる（銭-3・威信+3・天下点+1）',
          then: [
            { t: 'lose', res: 'zeni', n: 3 },
            { t: 'gain', res: 'ishin', n: 3 },
            { t: 'gain', res: 'tenka', n: 1 },
          ],
        },
        {
          label: '国もとで軍馬と兵を検分する（兵+2・次の出目+1）',
          then: [
            { t: 'gain', res: 'hei', n: 2 },
            { t: 'diceMod', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1581年、織田信長は京都の内裏東で大規模な馬揃え（軍装と軍馬を並べる行事）を行い、正親町天皇が上覧した。戦わずに力を示す催しで、都の人びとが見物した。',
    detail: {
      1: '1581年、織田信長は京都で「馬揃え」という大きな行列をおこないました。家来たちが着かざって馬をならべ、天皇もごらんになりました。戦わずに力を見せる催しで、都の人たちがおおぜい見物にきました。',
      2: '天正9年2月28日（西暦1581年4月1日）、織田信長は京都の内裏の東に馬場を設けて大規模な馬揃えを行った。丹羽長秀・柴田勝家・明智光秀ら織田家の諸将が着飾って隊を組み、正親町天皇が上覧した。奉行を務めたのは明智光秀である。馬揃えは本来、軍馬と軍装を検分する行事だが、このときは朝廷と都の人びとに織田政権の力を示す催しとなった。見物には公家や町衆が集まり、女性の見物も記録されている。合戦だけでなく、こうした儀礼や見せる行為も、当時の政治の重要な手段だった。',
      3: '天正9年2月28日（西暦1581年4月1日）、織田信長は京都の内裏東に広い馬場を設け、大規模な馬揃えを行った。『信長公記』巻十四によれば、丹羽長秀・柴田勝家・明智光秀ら諸将がそれぞれ隊を組み、趣向を凝らした装束と馬具で列を成した。正親町天皇が上覧し、近衛前久ら馬術に通じた公家も参加を許されている。奉行を務めたのは明智光秀で、催しの準備と進行を差配した。馬揃えは本来、軍馬と軍装を検分する軍事行事だが、この催しは朝廷に対して織田政権の実力を示し、同時に都の人びとに可視化する政治的な意味を持ったと解される。信長の意図については、朝廷への示威、家臣団の統制の誇示、正親町天皇への譲位の働きかけなど複数の解釈があるが、意図を直接示す史料はない。同年3月には再度の馬揃えが行われ、9月には第二次天正伊賀の乱で伊賀国が攻められている。見物には公家・僧侶・町衆が集まり、女性の見物も記録されており、当時の京都ではこうした催しが都市の人びとの見世物としても受け止められていた。合戦の記録に偏りがちな戦国の政治像に対して、儀礼・行列・贈答といった「見せる」行為が果たした役割は、近年の研究で重視されている。',
    },
    year: 1581,
    keywords: ['京都御馬揃え', '正親町天皇', '明智光秀', '内裏', '儀礼', '町衆'],
    caveat:
      '馬揃えの意図については、朝廷への示威、家臣団の統制の誇示、正親町天皇への譲位の働きかけなど複数の解釈がありますが、信長の意図を直接示す史料はありません。参加した人数や規模も『信長公記』などの記述に依るもので、正確な数は分かっていません。',
    gameNote:
      'この事件札は、合戦を経ずに威信を得る手段として置いています。あわせて断っておくと、本作の第IV章にある本能寺の変(1582)は「天下点1位が打撃を受ける」というゲーム機構ですが、これは独走を抑えるための逆転装置であって、明智光秀が何を動機に行動したかを説明するものではありません。光秀の動機については怨恨説・野望説・朝廷関与説・四国政策説などがあり、いずれも決め手を欠いています。この馬揃えの奉行が光秀だったことと本能寺の変とを因果で結ぶ史料もありません。',
  },
};

// ---------------------------------------------------------------------------
// 章のデッキ
// ---------------------------------------------------------------------------

/** 章の開始時に必ず発動する時代法。年代順。 */
export const CHAPTER3_ERA_LAWS: readonly HistoricalEvent[] = [
  NOBUNAGA_JOURAKU,
  MUROMACHI_BAKUFU_METSUBOU,
  NAGASHINO,
  AZUCHIJOU,
];

/** 評定マスで引く事件札。年代順。 */
export const CHAPTER3_INCIDENTS: readonly HistoricalEvent[] = [
  HIEIZAN_YAKIUCHI,
  EV_RAKUICHI_RAKUZA,
  OTATE_NO_RAN,
  ISHIYAMA_KASSEN_SHUUKETSU,
  KYOTO_ONMAZOROE,
];

/** 第III章のイベント全件。年表表示と図鑑に使う。年代順。 */
export const CHAPTER3_EVENTS: readonly HistoricalEvent[] = [
  NOBUNAGA_JOURAKU,
  HIEIZAN_YAKIUCHI,
  MUROMACHI_BAKUFU_METSUBOU,
  NAGASHINO,
  AZUCHIJOU,
  EV_RAKUICHI_RAKUZA,
  OTATE_NO_RAN,
  ISHIYAMA_KASSEN_SHUUKETSU,
  KYOTO_ONMAZOROE,
];
